import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    ViewEncapsulation,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import {
    IPermission,
    IRole,
    ODataResponse,
} from '@models';
import {
    PermissionService,
    RoleService,
} from '@services';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    Subject,
} from 'rxjs';
import {
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

import {
    IRoleOption,
    MODERN_FOLDERS_DEFAULT_ROLES,
} from './ui-folders-default-roles.constants';

const ROLE_FILTER = MODERN_FOLDERS_DEFAULT_ROLES
    .map(({ name }) => `DisplayName eq \'${name}\'`)
    .join(' or ');

@Component({
    selector: 'ui-folders-default-roles',
    templateUrl: './ui-folders-default-roles.component.html',
    styleUrls: ['./ui-folders-default-roles.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        RoleService,
    ],
})
export class UiFoldersDefaultRolesComponent implements OnDestroy {
    public fetch$ = new BehaviorSubject(void 0);
    public canViewRoles$ = this._appState.user.permissions.isGranted('Roles.View');

    public roles$: Observable<IRoleOption[]> = this.fetch$
        .pipe(
            switchMap(_ => this._roleService.get({
                top: MODERN_FOLDERS_DEFAULT_ROLES.length,
                expand: ['Permissions'],
                filter: `( ${ROLE_FILTER} )`,
            })),
            switchMap(results => this._rolesWithFilteredPermissions$(results)),
        );

    private _defaultRoles$ = new BehaviorSubject(cloneDeep(MODERN_FOLDERS_DEFAULT_ROLES));
    private _destroyed$ = new Subject();

    private _filteredDefaultPermissions$ = combineLatest([
        this._permissionService.get()
            .pipe(
                map(({ data }) => new Set(data.map(p => p.name))),
            ),
        this._defaultRoles$,
    ]).pipe(
        map(
            ([permissionsSet, defaults]) => defaults
                .map(role => ({
                    ...role,
                    permissions: role.permissions
                        .filter(permission => permissionsSet.has(permission.name)),
                })),
        ),
    );

    constructor(
        private _appState: AppStateFacade,
        private _roleService: RoleService,
        private _permissionService: PermissionService,
    ) { }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public create(role: IRoleOption) {
        role.status = 'pending';
        this._patchRoles(role);

        this._roleService.add({
            name: role.name,
            displayName: role.name,
            permissions: role.permissions,
        }).pipe(
            takeUntil(this._destroyed$),
        ).subscribe(
            _ => {
                role.status = 'created';
                this._patchRoles(role);
            },
            () => this.fetch$.next(void 0),
        );
    }

    private _rolesWithFilteredPermissions$ = (results: ODataResponse<IRole>) =>
        this._filteredDefaultPermissions$
            .pipe(
                map(defaults =>
                    defaults.map(role => {
                        const existingRole = results.data.find(r => r.name === role.name);

                        if (!existingRole) { return role; }

                        const missingPermissions = this._findMissingPermissions(role, existingRole);

                        return {
                            ...role,
                            ...(missingPermissions.length
                                ? {
                                    status: 'incomplete',
                                    missingPermissions: this._permissionsToString(missingPermissions),
                                }
                                : { status: 'created' }),
                        } as IRoleOption;
                    }),
                ),
            )

    private _patchRoles = (role: IRoleOption) =>
        this._defaultRoles$.next(
            this._defaultRoles$.getValue()
                .map(r => r.name === role.name ? role : r),
        )

    private _permissionsToString = (permissions: IPermission[]) => permissions.map(p => p.name).join(', ');

    private _findMissingPermissions = (role: IRoleOption, existingRole: IRole) =>
        role.permissions
            .filter(perm =>
                !existingRole.permissions
                    .find(p => p.name === perm.name && p.isGranted),
            )
}
